import { Link } from '@tanstack/react-router';
import { CalendarPlus, Columns3, Inbox, Send } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import type { ComparisonView, HoldRowView, HoldState } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Popover } from '../../../components/Popover';
import { ToneChip, type Tone } from '../../../components/Tone';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { formatDate } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { usePipelineOptions } from '../../pipeline/api/pipeline-api';
import { CONTACT_ATTEMPTS_CHECKED_BY_SERVER } from '../../pipeline/move-intent';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import type { ComparisonActions } from '../hooks/use-comparison-actions';
import styles from './Compare.module.css';

// A hold that has run out needs a person now (§L18: never released automatically).
const STATE_TONES: Readonly<Record<HoldState, Tone>> = { active: 'hold', ending_soon: 'attention', ended: 'urgent' };

/**
 * §L18 on DEC-02: every held silver medalist on the job, with one click each to release
 * to an offer (when a place is free) or, with Talent pools on, to a pool with its regret letter (F12.11) — and
 * "keep 30 more days" (S22 Q7). A hold that has run its 30 days says so and waits for a
 * person: auto-release never happens.
 */
export function HoldList({ view, actions }: { readonly view: ComparisonView; readonly actions: ComparisonActions }): ReactNode {
  const [pooling, setPooling] = useState<{ readonly row: HoldRowView; readonly anchor: AnchorRect } | null>(null);
  const isPoolsOn = useModule('pools');
  // §L33 (R8): with Offers off the same release moves them to Selected, with no offer draft.
  const isOffersOn = useModule('offers');
  const options = usePipelineOptions(view.can.pool && isPoolsOn);
  if (view.holds.length === 0) return null;

  return (
    <Card title={`On hold (${view.holds.length})`} meta="Silver medalists. Released by a person, never automatically.">
      <ul className={styles.holds}>
        {view.holds.map((row) => {
          const pending = actions.pendingOf(row.applicationId);
          const isEnded = row.hold.state === 'ended';
          return (
            <li key={row.applicationId} className={isEnded ? styles.holdEnded : styles.hold}>
              <div className={styles.holdWho}>
                <Link to="/applications/$applicationId" params={{ applicationId: row.applicationId }} className={styles.name}>
                  {row.candidateName}
                </Link>
                <span className={styles.quiet}>
                  Held from {row.hold.heldFromLabel}
                  {row.hold.placedByName ? ` by ${row.hold.placedByName}` : ''} · {formatDate(row.hold.placedAt)}
                  {row.hold.extensionCount > 0 ? ` · kept longer ${row.hold.extensionCount === 1 ? 'once' : `${row.hold.extensionCount} times`}` : ''}
                </span>
              </div>
              <ToneChip tone={STATE_TONES[row.hold.state]}>
                {isEnded ? 'Hold ended — decide' : row.hold.state === 'ending_soon' ? `Ends ${formatDate(row.hold.expiresAt)}` : `Until ${formatDate(row.hold.expiresAt)}`}
              </ToneChip>
              {view.can.release ? (
                <div className={styles.holdActions}>
                  <Button variant="solid" size="sm" icon={Send} disabled={row.offerBlockedReason !== null || pending !== null} isPending={pending === 'release'} title={row.offerBlockedReason ?? undefined} onClick={() => actions.releaseToOffer(row)}>
                    {isOffersOn ? 'Release to offer' : 'Select'}
                  </Button>
                  {isPoolsOn && (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={Inbox}
                      disabled={!view.can.pool || pending !== null}
                      title={view.can.pool ? undefined : "You can't add people to talent pools."}
                      onClick={(event: MouseEvent<HTMLButtonElement>) => setPooling({ row, anchor: anchorFrom(event.currentTarget) })}
                    >
                      Release to pool
                    </Button>
                  )}
                  <Button variant={isEnded ? 'outline' : 'ghost'} size="sm" icon={CalendarPlus} disabled={pending !== null} isPending={pending === 'extend'} onClick={() => actions.extend(row)}>
                    Keep 30 more days
                  </Button>
                  {!row.isInComparison && view.can.arrange && (
                    <Button variant="ghost" size="sm" icon={Columns3} disabled={pending !== null} isPending={pending === 'add'} onClick={() => actions.add(row.applicationId, row.candidateName)}>
                      Compare
                    </Button>
                  )}
                </div>
              ) : (
                <span className={styles.quiet}>A recruiter or HR releases holds.</span>
              )}
              {view.can.release && row.offerBlockedReason && <span className={styles.why}>{row.offerBlockedReason}</span>}
            </li>
          );
        })}
      </ul>

      <Popover anchor={pooling?.anchor ?? null} onClose={() => setPooling(null)} align="right" label={pooling ? `Release ${pooling.row.candidateName} to a pool` : 'Release to a pool'}>
        {pooling && (
          <ReasonPicker
            mode="pool"
            reasons={view.reasons.map((reason) => ({ ...reason, minimumContactAttempts: null }))}
            pools={options.data?.pools ?? []}
            canPool={view.can.pool}
            contactAttempts={CONTACT_ATTEMPTS_CHECKED_BY_SERVER}
            candidateName={pooling.row.candidateName}
            onConfirm={(choice) => {
              actions.releaseToPool(pooling.row, choice);
              setPooling(null);
            }}
          />
        )}
      </Popover>
    </Card>
  );
}
